import React, { useEffect, useState } from "react";
import {
  Calendar,
  User,
  Car,
  FileText,
  Shield,
  CircleChevronLeft,
} from "lucide-react";
import { CompleteDriverDtls } from "./DriverTypes";

import { FormInput } from "./FormInput";
import { ImageGrid } from "./ImageUpload";
import toast from "react-hot-toast";
import { ClinicDriverService } from "../../../../../services/api/clinic.driver.service";

// Main Component
// Main Component
const DriverUpdateComponent: React.FC<{
  driverId: string | null;
  onClose: () => void;
  isUpdate: boolean;
}> = ({ driverId, onClose, isUpdate }) => {
  const [driver, setDriver] = useState<CompleteDriverDtls>({
    id: "",
    clinicId: "",
    profilePicture: "",
    education: "",
    dateOfBirth: "",
    address: "",
    aadharFrontImage: "",
    aadharBackImage: "",
    dlFrontImage: "",
    dlBackImage: "",
    vehicleRcFront: "",
    vehicleRcBack: "",
    vehicleBrand: "",
    vehicleNumber: "",
    drivingLicence: "",
    vehicleInsurance: "",
    blocked: false,
    isAvailable: true,
    lastOrderTime: "",
    type: "",
    username: "",
    name: "",
    email: "",
    mobile_number: "",
    aadhar_number: "",
    role: "DRIVER",
  });

  const formatDate = (isoDate: string) => isoDate.split("T")[0];

  const fetchExistingDriverData = async () => {
    try {
      if (!driverId) {
        toast.error("Driver ID not defined");
        return;
      }
      const res = await ClinicDriverService.getDriverDetails(driverId);
      const data = res.data;

      if (data) {
        setDriver({
          ...driver,
          ...data,
          mobile_number: data.mobileNumber || "",
          aadhar_number: data.aadharNumber || "",
          email: data.email || "",
          dateOfBirth: data.dateOfBirth ? formatDate(data.dateOfBirth) : "",
        });
      }
    } catch (error) {
      console.error("Error fetching driver details:", error);
      toast.error("Error in fetching driver details");
    }
  };

  useEffect(() => {
    fetchExistingDriverData();
  }, []);

  const [images, setImages] = useState<{ [key: string]: File | null }>({});

  const handleImageChange = (fieldName: string, file: File | null) => {
    setImages((prev) => ({ ...prev, [fieldName]: file }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (!driverId) {
        toast.error("Driver ID not defined");
        return;
      }

      // Prepare the data object to be sent
      const data = {
        username: driver.username,
        name: driver.name,
        email: driver.email,
        mobileNumber: driver.mobile_number,
        aadharNumber: driver.aadhar_number,
        education: driver.education,
        dateOfBirth: driver.dateOfBirth,
        address: driver.address,
        vehicleBrand: driver.vehicleBrand,
        vehicleNumber: driver.vehicleNumber,
        drivingLicence: driver.drivingLicence,
        type: driver.type,
        // Add any other fields that need to be updated
      };

      // Filter out null values from images
      const filesToUpload: Record<string, File> = {};
      for (const [key, file] of Object.entries(images)) {
        if (file) {
          filesToUpload[key] = file;
        }
      }

      // Call the update function
      const res = await ClinicDriverService.updateDriver(
        driverId,
        data,
        filesToUpload
      );

      console.log(res);

      console.log(data, filesToUpload);

      toast.success("Driver updated successfully");
      onClose(); // Close the form after successful update
    } catch (error) {
      console.error("Error updating driver:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to update driver"
      );
    }
  };

  const imageFields = [
    {
      label: "Profile Picture",
      key: "profilePicture",
      src: driver.profilePicture || "",
    },
    {
      label: "Aadhar Front Image",
      key: "aadharFrontImage",
      src: driver.aadharFrontImage,
      required: true,
    },
    {
      label: "Aadhar Back Image",
      key: "aadharBackImage",
      src: driver.aadharBackImage,
      required: true,
    },
    {
      label: "DL Front Image",
      key: "dlFrontImage",
      src: driver.dlFrontImage,
      required: true,
    },
    {
      label: "DL Back Image",
      key: "dlBackImage",
      src: driver.dlBackImage,
      required: true,
    },
    {
      label: "Vehicle RC Front",
      key: "vehicleRcFront",
      src: driver.vehicleRcFront,
      required: true,
    },
    {
      label: "Vehicle RC Back",
      key: "vehicleRcBack",
      src: driver.vehicleRcBack,
      required: true,
    },
    {
      label: "Vehicle Insurance",
      key: "vehicleInsurance",
      src: driver.vehicleInsurance,
      required: true,
    },
  ];

  return (
    <div className="w-full h-full bg-white py-4">
      <h1
        onClick={onClose}
        className="flex items-center justify-start gap-2 cursor-pointer"
      >
        {
          <CircleChevronLeft className="w-6 h-6 bg-[#808080] text-white rounded-full " />
        }
      </h1>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          {isUpdate && "Update"} Driver Information
        </h1>
        <p className="text-gray-600">{isUpdate? "Update driver":"Driver"} details and documentation</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Form Fields */}
          <div className="space-y-6">
            {/* Personal Information */}
            <div className="bg-gray-50 p-6 rounded-lg border">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <User className="w-5 h-5 mr-2" />
                Personal Information
              </h2>

              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  label="Full Name"
                  value={driver.name}
                  onChange={(value) =>
                    setDriver((prev) => ({ ...prev, name: value }))
                  }
                  placeholder="Enter full name"
                  required
                  icon={<User className="w-4 h-4" />}
                  disabled={!isUpdate}
                />

                <FormInput
                  label="Username"
                  value={driver.username}
                  onChange={(value) =>
                    setDriver((prev) => ({ ...prev, username: value }))
                  }
                  placeholder="Enter username"
                  required
                  icon={<User className="w-4 h-4" />}
                  disabled={!isUpdate}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  label="Mobile Number"
                  type="tel"
                  value={driver.mobile_number}
                  onChange={(value) =>
                    setDriver((prev) => ({ ...prev, mobile_number: value }))
                  }
                  placeholder="Enter mobile number"
                  required
                  icon={<User className="w-4 h-4" />}
                  disabled={!isUpdate}
                />

                <FormInput
                  label="Aadhar Number"
                  value={driver.aadhar_number || ""}
                  onChange={(value) =>
                    setDriver((prev) => ({ ...prev, aadhar_number: value }))
                  }
                  placeholder="Enter Aadhar number"
                  icon={<FileText className="w-4 h-4" />}
                  disabled={!isUpdate}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  label="Email"
                  type="email"
                  value={driver.email || ""}
                  onChange={(value) =>
                    setDriver((prev) => ({ ...prev, email: value }))
                  }
                  placeholder="Enter email address"
                  icon={<User className="w-4 h-4" />}
                  disabled={!isUpdate}
                />
                <FormInput
                  label="Date of Birth"
                  type="date"
                  value={driver.dateOfBirth}
                  onChange={(value) =>
                    setDriver((prev) => ({ ...prev, dateOfBirth: value }))
                  }
                  required
                  icon={<Calendar className="w-4 h-4" />}
                  disabled={!isUpdate}
                />
              </div>

              <FormInput
                label="Education"
                value={driver.education || ""}
                onChange={(value) =>
                  setDriver((prev) => ({ ...prev, education: value }))
                }
                placeholder="Enter education details"
                icon={<FileText className="w-4 h-4" />}
                disabled={!isUpdate}
              />

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={driver.address}
                  disabled={!isUpdate}
                  onChange={(e) =>
                    setDriver((prev) => ({
                      ...prev,
                      address: e.target.value,
                    }))
                  }
                  placeholder="Enter complete address"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  required
                />
              </div>
            </div>

            {/* Vehicle Information */}
            <div className="bg-gray-50 p-6 rounded-lg border">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <Car className="w-5 h-5 mr-2" />
                Vehicle Information
              </h2>

              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  label="Vehicle Brand"
                  value={driver.vehicleBrand || ""}
                  onChange={(value) =>
                    setDriver((prev) => ({ ...prev, vehicleBrand: value }))
                  }
                  placeholder="Enter Vehicle Brand"
                  icon={<Car className="w-4 h-4" />}
                  disabled={!isUpdate}
                />
                <FormInput
                  label="Vehicle Number"
                  value={driver.vehicleNumber}
                  onChange={(value) =>
                    setDriver((prev) => ({ ...prev, vehicleNumber: value }))
                  }
                  placeholder="Enter vehicle number"
                  required
                  icon={<Car className="w-4 h-4" />}
                  disabled={!isUpdate}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  label="Driving License Number"
                  value={driver.drivingLicence}
                  onChange={(value) =>
                    setDriver((prev) => ({ ...prev, drivingLicence: value }))
                  }
                  placeholder="Enter driving license number"
                  required
                  icon={<Shield className="w-4 h-4" />}
                  disabled={!isUpdate}
                />
              </div>
            </div>

            {/* Action Buttons */}
          </div>

          {/* Right Column - Image Uploads */}
          <div className="space-y-4">
            <div className="bg-gray-50 p-6 rounded-lg border">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <FileText className="w-5 h-5 mr-2" />
                Document Images
              </h2>

              <ImageGrid
                images={imageFields.map((field) => ({
                  label: field.label,
                  src: field.src,
                  onChange: (file) => handleImageChange(field.key, file),
                  required: field.required,
                }))}
                disabled={!isUpdate}
              />
            </div>
          </div>
        </div>
        {isUpdate && (
          <div className="flex justify-end gap-4 mt-8 mb-8 pb-4 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
            >
              Update Driver
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default DriverUpdateComponent;
